import React, { ReactNode } from 'react';
import { useRBAC } from '../../RbacProvider';
import NoAccessWidget from '../../../components/no-access-widget/NoAccessWidget';
import NoAccessComponent from '../../../components/no-access-widget/NoAccessComponent';
import './ProtectComponent.scss';

interface ProtectComponentProps {
    resource: string;
    action: string;
    children: ReactNode;
    fallback?: ReactNode;
    widgetName?: string;
    widgetMessage?: string;
    showInWidget?: boolean;
    widgetMessageName?: string;
    graphViewHeader?: string;
    disable?: boolean;
    assetDashboard?: boolean;
}

/**
 * A component that conditionally renders its children based on the user's RBAC permissions
 */
export const ProtectComponent: React.FC<ProtectComponentProps> = ({
    resource,
    action,
    children,
    fallback,
    widgetName = 'Widget',
    widgetMessage = `You do not currently have access to this Component, please speak with your site Administrator.`,
    showInWidget = false,
    widgetMessageName = '',
    graphViewHeader = '',
    disable = false,
    assetDashboard = false,
}) => {

    if (disable) {
        return <>{children}</>;
    }
    const { can, loading } = useRBAC();

    // If still loading permissions, show a loading indicator
    if (loading) {
        return null;
    }

    if (widgetMessageName) {
        widgetMessage = `You do not currently have access to view ${widgetMessageName}, please speak with your site Administrator.`;
    }

    // Check if the user has permission to access this resource
    const hasAccess = can(resource, action);

    if (!hasAccess) {
        // If there's a custom fallback, use it
        if (fallback) {
            return <>{fallback}</>;
        }

        if (assetDashboard) {
            return (
                <div className='asset-no-access-wrapper'>
                    <NoAccessComponent
                        message={widgetMessage}
                    />
                </div>
            );
        }

        if (graphViewHeader) {
            widgetMessage = `You do not currently have access to view ${graphViewHeader}, please speak with your site Administrator.`;
            return (
                <div className='pt-12 pb-12 pl-8 pr-8'>
                    <h2 style={{ fontSize: '17px', fontWeight: 600 }}>{graphViewHeader}</h2>
                    <div className='p-8'>
                        <NoAccessComponent
                            message={widgetMessage}
                        />
                    </div>
                </div >
            );

        }

        if (showInWidget) {
            // Show a simplified NoAccessComponent for use within widgets
            return (
                <NoAccessComponent
                    message={widgetMessage}
                />
            );
        }

        // Otherwise, show the default NoAccessWidget
        return (
            <NoAccessWidget
                widgetName={widgetName}
                widgetMessage={widgetMessage}
                infoIcon={true}
            />
        );
    }

    // User has access, render the children
    return <>{children}</>;
};
